import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';

// Autocomplete de responsavel — busca em dbo.rh_funcionarios (tabela do
// sistema-rh, mesmo BDCENTRAO) via GET /equipamentos/funcionarios. Aceita
// texto livre; a lista serve so pra facilitar a escolha.
function ResponsavelAutocomplete({ value, onChange, disabled }) {
  const [aberto, setAberto] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [termo, setTermo] = useState(value || '');
  const rootRef = useRef(null);
  const debounceRef = useRef(null);

  // Mantem input sincronizado com o value externo (ex.: quando o form abre
  // pra editar um equipamento existente).
  useEffect(() => { setTermo(value || ''); }, [value]);

  // Fecha ao clicar fora do container.
  useEffect(() => {
    const handler = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const buscar = (t) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setCarregando(true);
      try {
        const res = await axios.get(`${API}/equipamentos/funcionarios`, {
          params: t ? { busca: t } : {},
        });
        setResultados(res.data?.data || []);
      } catch {
        setResultados([]);
      } finally {
        setCarregando(false);
      }
    }, 300);
  };

  const handleFocus = () => {
    setAberto(true);
    if (resultados.length === 0) buscar(termo);
  };

  const handleInput = (novoTermo) => {
    setTermo(novoTermo);
    onChange(novoTermo);
    setAberto(true);
    buscar(novoTermo);
  };

  const selecionar = (f) => {
    setTermo(f.nome);
    onChange(f.nome);
    setAberto(false);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <input
        type="text"
        className="form-control"
        value={termo}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={handleFocus}
        maxLength={255}
        placeholder="Digite para buscar em RH ou preencha manualmente"
        disabled={disabled}
        autoComplete="off"
      />
      {aberto && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
            maxHeight: 240, overflowY: 'auto', zIndex: 1100,
            background: 'var(--portal-surface)',
            border: '1px solid var(--portal-border)',
            borderRadius: 8, boxShadow: '0 6px 20px rgba(8,23,43,0.18)',
          }}
        >
          {carregando ? (
            <div style={{ padding: 10, fontSize: '0.82rem', color: 'var(--portal-muted)' }}>
              Buscando...
            </div>
          ) : resultados.length === 0 ? (
            <div style={{ padding: 10, fontSize: '0.82rem', color: 'var(--portal-muted)' }}>
              Nenhum funcionario encontrado. Voce pode digitar o nome manualmente.
            </div>
          ) : (
            resultados.map((f) => (
              <button
                key={f.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); selecionar(f); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 12px', border: 'none', background: 'transparent',
                  borderBottom: '1px solid var(--portal-border)', cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--portal-surface-soft)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ fontSize: '0.88rem', color: 'var(--portal-text)', fontWeight: 500 }}>
                  {f.nome}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
                  {[f.cargo, f.filial].filter(Boolean).join(' — ') || 'sem cargo/filial'}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const FORM_VAZIO = {
  apelido: '',
  modelo: '',
  filial: '',
  tipo: '',
  mac: '',
  numeroChip: '',
  imei: '',
  responsavel: '',
  ativo: false,
  autoUsoExterno: false,
};

// Cabecalho ordenavel (mesmo padrao usado em Indicadores.js).
function Th({ col, label, sortCol, sortDir, onSort, align, style }) {
  const ativo = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      style={{
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
        textAlign: align || 'left',
        color: ativo ? 'var(--portal-text)' : undefined,
        ...(style || {}),
      }}
    >
      {label}
      {ativo
        ? <span style={{ marginLeft: 4, opacity: 0.7 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>
        : <span style={{ marginLeft: 4, opacity: 0.25 }}>↕</span>}
    </th>
  );
}

// Comparador generico: nulos vao pro fim, boolean vira 0/1, string usa
// localeCompare (case-insensitive/PT-BR), numero como numero. Sinal e
// invertido no chamador quando sortDir === 'desc'.
function comparar(a, b) {
  const vazioA = a === null || a === undefined || a === '';
  const vazioB = b === null || b === undefined || b === '';
  if (vazioA && vazioB) return 0;
  if (vazioA) return 1;
  if (vazioB) return -1;
  if (typeof a === 'boolean' || typeof b === 'boolean') return (a ? 1 : 0) - (b ? 1 : 0);
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base' });
}

// Tela Equipamentos: CRUD sobre a tabela COMPARTILHADA
// dbo.DispositivosEletronicos (mesma do sistema-suc). A tabela antiga
// dbo.siti_equipamentos foi preservada como legado (esta vazia e nao e
// mais lida). Ver CONTEXTO.md secao "Modulo de Equipamentos".
export default function Equipamentos() {
  const { temPermissao } = useAuth();
  const podeUsar = temPermissao('ti:equipamentos');

  const [equipamentos, setEquipamentos] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState('');

  const [sortCol, setSortCol] = useState('apelido');
  const [sortDir, setSortDir] = useState('asc');
  const onSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sorted = useMemo(() => {
    const arr = [...equipamentos];
    arr.sort((a, b) => {
      const cmp = comparar(a[sortCol], b[sortCol]);
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return arr;
  }, [equipamentos, sortCol, sortDir]);

  // Resumo derivado dos equipamentos ja filtrados no backend pelo termo de
  // busca — muda automaticamente conforme o filtro.
  const resumo = useMemo(() => {
    const total = equipamentos.length;
    let ativos = 0;
    let usoExterno = 0;
    let semResponsavel = 0;
    const porTipo = new Map();
    const porFilial = new Map();
    for (const e of equipamentos) {
      if (e.ativo) ativos += 1;
      if (e.autoUsoExterno) usoExterno += 1;
      if (!e.responsavel || !String(e.responsavel).trim()) semResponsavel += 1;
      const tipo = (e.tipo || '').trim() || '(sem tipo)';
      porTipo.set(tipo, (porTipo.get(tipo) || 0) + 1);
      const filial = (e.filial || '').trim() || '(sem filial)';
      porFilial.set(filial, (porFilial.get(filial) || 0) + 1);
    }
    const topTipo = [...porTipo.entries()].sort((a, b) => b[1] - a[1])[0];
    const topFilial = [...porFilial.entries()].sort((a, b) => b[1] - a[1])[0];
    return {
      total,
      ativos,
      inativos: total - ativos,
      usoExterno,
      semResponsavel,
      tiposDistintos: porTipo.size,
      filiaisDistintas: porFilial.size,
      topTipo,
      topFilial,
    };
  }, [equipamentos]);

  const pct = (n) => (resumo.total === 0 ? 0 : Math.round((n / resumo.total) * 100));

  const carregar = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/equipamentos`, { params: busca ? { busca } : {} }),
        axios.get(`${API}/equipamentos/filiais`).catch(() => ({ data: { data: [] } })),
      ]);
      setEquipamentos(r1.data?.data || []);
      setFiliais(r2.data?.data || []);
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Nao foi possivel carregar os equipamentos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!podeUsar) { setLoading(false); return; }
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeUsar, busca]);

  const abrirNovo = () => {
    setEditando(null);
    setForm(FORM_VAZIO);
    setErroForm('');
    setModalAberto(true);
  };

  const abrirEditar = (equipamento) => {
    setEditando(equipamento);
    setForm({
      apelido: equipamento.apelido || '',
      modelo: equipamento.modelo || '',
      filial: equipamento.filial || '',
      tipo: equipamento.tipo || '',
      mac: equipamento.mac || '',
      numeroChip: equipamento.numeroChip || '',
      imei: equipamento.imei || '',
      responsavel: equipamento.responsavel || '',
      ativo: !!equipamento.ativo,
      autoUsoExterno: !!equipamento.autoUsoExterno,
    });
    setErroForm('');
    setModalAberto(true);
  };

  const salvar = async () => {
    setSalvando(true);
    setErroForm('');
    try {
      const payload = { ...form };
      if (editando) {
        await axios.put(`${API}/equipamentos/${editando.id}`, payload);
      } else {
        await axios.post(`${API}/equipamentos`, payload);
      }
      setModalAberto(false);
      await carregar();
    } catch (e) {
      setErroForm(e.response?.data?.message || 'Nao foi possivel salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (equipamento) => {
    const ok = await Swal.fire({
      icon: 'warning',
      title: 'Excluir equipamento?',
      html: `<strong>${equipamento.apelido}</strong> sera removido.<br>` +
            '<span style="color:#dc3545;font-size:.85rem">Este equipamento tambem some do sistema-suc (base compartilhada).</span>',
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b43737',
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${API}/equipamentos/${equipamento.id}`);
      await carregar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.response?.data?.message || e.message });
    }
  };

  if (!podeUsar) {
    return (
      <div className="content-shell">
        <div className="feedback-banner feedback-banner--error">
          Voce nao tem permissao para acessar equipamentos.
        </div>
      </div>
    );
  }

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">TI</span>
          <h1>Equipamentos</h1>
          <p>
            Cadastro de ativos (celulares, tablets, notebooks, coletores, etc.).
            Base compartilhada com o sistema-suc — alteracoes feitas aqui
            aparecem la e vice-versa.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={abrirNovo}
            style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
          >
            <i className="bi bi-plus-lg me-1" /> Novo equipamento
          </button>
        </div>
      </section>

      <div className="filter-card">
        <div className="filter-card__field filter-card__field--grow">
          <i className="bi bi-search" />
          <input
            type="search"
            placeholder="Buscar por apelido, modelo, mac, imei, chip, responsavel ou tipo..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>

      {!loading && !erro && (
        <section
          className="stats-grid stats-grid--compact stats-grid--dashboard"
          aria-label="Resumo dos equipamentos filtrados"
          style={{ marginBottom: 18 }}
        >
          <div className="stat-card stat-card--compact">
            <span>{busca ? 'Resultados do filtro' : 'Total cadastrado'}</span>
            <strong>{resumo.total}</strong>
            <small>
              {resumo.tiposDistintos} tipo{resumo.tiposDistintos === 1 ? '' : 's'}
              {' · '}
              {resumo.filiaisDistintas} filia{resumo.filiaisDistintas === 1 ? 'l' : 'is'}
            </small>
          </div>
          <div className="stat-card stat-card--compact stat-card--success">
            <span>Ativos</span>
            <strong>{resumo.ativos}</strong>
            <small>
              {pct(resumo.ativos)}% do total
              {resumo.inativos > 0 ? ` · ${resumo.inativos} inativo${resumo.inativos === 1 ? '' : 's'}` : ''}
            </small>
          </div>
          <div className="stat-card stat-card--compact stat-card--accent">
            <span>Uso externo autorizado</span>
            <strong>{resumo.usoExterno}</strong>
            <small>{pct(resumo.usoExterno)}% do total</small>
          </div>
          <div className="stat-card stat-card--compact stat-card--warning">
            <span>Sem responsavel</span>
            <strong>{resumo.semResponsavel}</strong>
            <small>
              {resumo.topTipo
                ? `Top tipo: ${resumo.topTipo[0]} (${resumo.topTipo[1]})`
                : 'sem dados'}
            </small>
          </div>
        </section>
      )}

      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}

      <section className="content-card">
        {loading ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando equipamentos...</p>
          </div>
        ) : equipamentos.length === 0 ? (
          <div className="empty-state">
            <strong>Nenhum equipamento cadastrado</strong>
            <span>Clique em "Novo equipamento" para comecar.</span>
          </div>
        ) : (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <Th col="id" label="ID" sortCol={sortCol} sortDir={sortDir} onSort={onSort} align="right" />
                  <Th col="apelido" label="Apelido" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                  <Th col="tipo" label="Tipo" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                  <Th col="modelo" label="Modelo" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                  <Th col="filial" label="Filial" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                  <Th col="mac" label="MAC" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                  <Th col="numeroChip" label="Chip" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                  <Th col="imei" label="IMEI" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                  <Th col="responsavel" label="Responsavel" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                  <Th col="ativo" label="Ativo" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                  <Th col="autoUsoExterno" label="Uso ext." sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                  <th style={{ textAlign: 'right' }}>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((e) => (
                  <tr key={e.id}>
                    <td className="cell-muted" style={{ fontFamily: 'monospace', textAlign: 'right' }}>{e.id}</td>
                    <td><span className="cell-strong">{e.apelido}</span></td>
                    <td className="cell-muted">{e.tipo || '-'}</td>
                    <td className="cell-muted">{e.modelo || '-'}</td>
                    <td className="cell-muted">{e.filial || '-'}</td>
                    <td className="cell-muted" style={{ fontFamily: 'monospace' }}>{e.mac || '-'}</td>
                    <td className="cell-muted" style={{ fontFamily: 'monospace' }}>{e.numeroChip || '-'}</td>
                    <td className="cell-muted" style={{ fontFamily: 'monospace' }}>{e.imei || '-'}</td>
                    <td className="cell-muted">{e.responsavel || '-'}</td>
                    <td>
                      <span className={`permissao-chip ${e.ativo ? 'permissao-chip--on' : ''}`}>
                        {e.ativo ? 'Sim' : 'Nao'}
                      </span>
                    </td>
                    <td>
                      <span className={`permissao-chip ${e.autoUsoExterno ? 'permissao-chip--on' : ''}`}>
                        {e.autoUsoExterno ? 'Sim' : 'Nao'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-1"
                        onClick={() => abrirEditar(e)}
                        title="Editar"
                      >
                        <i className="bi bi-pencil" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => excluir(e)}
                        title="Excluir equipamento"
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {modalAberto && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(8, 23, 43, 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            className="content-card"
            style={{
              width: 'min(720px, 96vw)', maxHeight: '90vh', padding: 0,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
          >
            <header style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px', borderBottom: '1px solid var(--portal-border)',
            }}>
              <strong>
                <i className={`bi ${editando ? 'bi-pencil' : 'bi-plus-circle'} me-2`} />
                {editando ? `Editar equipamento #${editando.id}` : 'Novo equipamento'}
              </strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setModalAberto(false)}
                disabled={salvando}
              >
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
              {erroForm && (
                <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.85rem' }}>
                  {erroForm}
                </div>
              )}

              <div className="row g-3" style={{ margin: 0 }}>
                <div className="col-12 col-sm-6" style={{ paddingLeft: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Apelido <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.apelido}
                    onChange={(e) => setForm({ ...form, apelido: e.target.value })}
                    maxLength={55}
                    placeholder="Ex: Celular Vendedor 03"
                    autoFocus
                  />
                </div>
                <div className="col-12 col-sm-6" style={{ paddingRight: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Tipo <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.tipo}
                    onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                    maxLength={55}
                    placeholder="Ex: celular, tablet, coletor, notebook"
                  />
                </div>
              </div>

              <div className="row g-3" style={{ margin: 0 }}>
                <div className="col-12 col-sm-8" style={{ paddingLeft: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Modelo <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.modelo}
                    onChange={(e) => setForm({ ...form, modelo: e.target.value })}
                    maxLength={255}
                    placeholder="Ex: Samsung Galaxy A15"
                  />
                </div>
                <div className="col-12 col-sm-4" style={{ paddingRight: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Filial <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.filial}
                    onChange={(e) => setForm({ ...form, filial: e.target.value })}
                  >
                    <option value="">Selecione...</option>
                    {form.filial && !filiais.some((f) => f.codigo === form.filial) && (
                      <option value={form.filial}>{form.filial} (fora do catalogo)</option>
                    )}
                    {filiais.map((f) => (
                      <option key={f.codigo} value={f.codigo}>
                        {f.codigo} - {f.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row g-3" style={{ margin: 0 }}>
                <div className="col-12 col-sm-4" style={{ paddingLeft: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>MAC</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.mac}
                    onChange={(e) => setForm({ ...form, mac: e.target.value })}
                    maxLength={17}
                    placeholder="AA:BB:CC:DD:EE:FF"
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>
                <div className="col-12 col-sm-4">
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>N° Chip</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.numeroChip}
                    onChange={(e) => setForm({ ...form, numeroChip: e.target.value })}
                    maxLength={17}
                    placeholder="Numero de telefone do chip"
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>
                <div className="col-12 col-sm-4" style={{ paddingRight: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>IMEI</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.imei}
                    onChange={(e) => setForm({ ...form, imei: e.target.value })}
                    maxLength={255}
                    placeholder="15 digitos"
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Responsavel</label>
                <ResponsavelAutocomplete
                  value={form.responsavel}
                  onChange={(nome) => setForm({ ...form, responsavel: nome })}
                  disabled={salvando}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <label
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 240px',
                    padding: '10px 12px', borderRadius: 10,
                    background: 'var(--portal-surface-soft)', border: '1px solid var(--portal-border)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!form.ativo}
                    onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                  />
                  <span style={{ fontSize: '0.85rem', color: 'var(--portal-text)' }}>
                    Ativo
                    <br />
                    <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                      Equipamento em uso na empresa.
                    </span>
                  </span>
                </label>

                <label
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 240px',
                    padding: '10px 12px', borderRadius: 10,
                    background: 'var(--portal-surface-soft)', border: '1px solid var(--portal-border)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!form.autoUsoExterno}
                    onChange={(e) => setForm({ ...form, autoUsoExterno: e.target.checked })}
                  />
                  <span style={{ fontSize: '0.85rem', color: 'var(--portal-text)' }}>
                    Autorizado uso externo
                    <br />
                    <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                      Pode ser usado fora da filial (rota externa/entrega).
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <footer style={{
              padding: '12px 18px', borderTop: '1px solid var(--portal-border)',
              display: 'flex', justifyContent: 'flex-end', gap: 8,
            }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setModalAberto(false)}
                disabled={salvando}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={salvar}
                disabled={salvando}
                style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
              >
                <i className="bi bi-check2 me-1" />
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
